import React, { useState, useEffect, useRef } from 'react';
import { VERSION_STRING } from '../version';
import { useWeb3 } from '../contexts/Web3Context';
import { useWallet } from '../contexts/WalletContext';
import { useUser } from '../contexts/UserContext';
import { ethers } from 'ethers';
import { HybridBrowserProvider } from '../utils/HybridBrowserProvider';
import TransactionConfirmModal from './TransactionConfirmModal';
import { useSearchParams } from 'react-router-dom';
import { useTokenPrice } from '../hooks/useTokenPrice';
import { FaGasPump, FaInfoCircle, FaShieldAlt } from 'react-icons/fa'; // Icons for Modal
import TransactionSuccessModal from './TransactionSuccessModal';
import TransactionProgressOverlay from './TransactionProgressOverlay';
import { requestNotificationPermission, sendTxNotification } from '../utils/txNotifications';

// API URL - usa proxy en producción, localhost en desarrollo
const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:5001' : '';

// Detectar si es móvil para ajustar timeouts
const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const QUOTE_TIMEOUT = isMobile ? 120 : 60; // 2 minutos en móvil, 1 minuto en desktop

/**
 * TABLA DE MÍNIMOS POR MONEDA
 * Valores mínimos permitidos para compra y venta de cada criptomoneda.
 * Esto evita transacciones muy pequeñas que no son rentables.
 * 
 * Formato: { [MONEDA]: { compra: number, venta: number } }
 */
const MINIMUM_AMOUNTS = {
  XCO: { compra: 0.1, venta: 0.1 },
  BNB: { compra: 0.001, venta: 0.001 },
  // Agregar más monedas aquí cuando se habiliten:
  // BTC: { compra: 0.0001, venta: 0.0001 },
  // ETH: { compra: 0.001, venta: 0.001 },
};

// Helper: Detect WalletConnect session errors (code 5300)
const isWalletConnectSessionError = (err) => {
  const msg = (err?.message || '') + (err?.reason || '');
  return msg.includes('5300') || msg.includes('Invalid Session Properties') || msg.includes('Proposal expired');
};

// Helper: Pre-validate chain is BSC before sending transaction
const validateChainBeforeTx = async (walletObj) => {
  if (!walletObj?.provider) throw new Error('Wallet no conectada');
  try {
    const chainIdRaw = await walletObj.provider.request({ method: 'eth_chainId' });
    const chainId = Number(chainIdRaw);
    if (chainId !== 56) {
      throw new Error(`Red incorrecta (chain ${chainId}). Desconecta y reconecta tu wallet en BSC.`);
    }
  } catch (e) {
    if (isWalletConnectSessionError(e)) {
      throw new Error('Sesión de wallet expirada. Por favor desconecta y reconecta tu wallet.');
    }
    throw e;
  }
};

// Helper: Wrap a promise with a timeout (ms). Rejects with errorMsg if time runs out.
const withTimeout = (promise, ms, errorMsg) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(errorMsg)), ms))
  ]);

const WALLET_TIMEOUT = 30000; // 30s for wallet signature response
const TX_TIMEOUT = 120000; // 120s for on-chain confirmation (BSC ~3s blocks, generous margin)

// Helper: Race WC approve() against on-chain allowance poll via public RPC.
// Solves: WalletConnect drops WebSocket when mobile browser is backgrounded,
// causing approve() to hang even though the tx was confirmed on-chain.
// Uses Date.now() for timeout so the poll always checks on-chain BEFORE timing out
// (Chrome throttles setTimeout in background tabs, causing pure setTimeout timeouts
// to fire before the poll can read the blockchain when the tab resumes).
const raceApproveWithPoll = async (approveCall, tokenContract, userAddr, spender, requiredAmountWei, timeoutMs) => {
  let stopped = false;
  const startTime = Date.now();

  // Record initial allowance to detect CHANGE (prevents false positive from residual allowance)
  const initialAllowance = await tokenContract.allowance(userAddr, spender);

  // Branch A: WC approve (may hang on mobile app switch)
  const wcPromise = approveCall().then(tx => ({ source: 'wc', tx }));

  // Branch B: On-chain poll via public RPC (always works via HybridBrowserProvider)
  const pollPromise = new Promise((resolve, reject) => {
    const check = async () => {
      if (stopped) return;
      try {
        const current = await tokenContract.allowance(userAddr, spender);
        if (current !== initialAllowance && current >= requiredAmountWei) {
          resolve({ source: 'poll', allowance: current });
          return;
        }
      } catch (_) { /* ignore poll errors */ }
      // Timeout check AFTER on-chain read (poll gets priority over timeout)
      if (Date.now() - startTime > timeoutMs) {
        reject(new Error('Tiempo de espera agotado. La wallet no respondió a tiempo. Intenta nuevamente.'));
        return;
      }
      if (!stopped) setTimeout(check, 3000);
    };
    setTimeout(check, 5000); // Start after 5s (give WC priority for normal flow)
  });

  try {
    return await Promise.race([wcPromise, pollPromise]);
  } finally {
    stopped = true;
  }
};

// Helper: Race WC tx call against on-chain balance change poll.
// Same Date.now() pattern: checks balance BEFORE checking timeout.
// checkFn: async () => boolean — returns true when on-chain state confirms tx executed.
const raceTxWithPoll = async (txCall, checkFn, timeoutMs) => {
  let stopped = false;
  const startTime = Date.now();

  // Branch A: WC tx call (may hang on mobile app switch)
  const wcPromise = txCall().then(tx => ({ source: 'wc', tx }));

  // Branch B: On-chain balance poll via public RPC
  const pollPromise = new Promise((resolve, reject) => {
    const check = async () => {
      if (stopped) return;
      try {
        const changed = await checkFn();
        if (changed) {
          resolve({ source: 'poll' });
          return;
        }
      } catch (_) { /* ignore poll errors */ }
      // Timeout check AFTER on-chain read (poll gets priority over timeout)
      if (Date.now() - startTime > timeoutMs) {
        reject(new Error('Tiempo de espera agotado. La wallet no respondió a tiempo. Intenta nuevamente.'));
        return;
      }
      if (!stopped) setTimeout(check, 3000);
    };
    setTimeout(check, 5000); // Start after 5s
  });

  try {
    return await Promise.race([wcPromise, pollPromise]);
  } finally {
    stopped = true;
  }
};

const TradingWidget = ({ cryptoData, initialState = {}, onSubmit, refreshBalances }) => {
  const { wallet, usdtContract, crowdsaleContract, buybackContract, bnbAtmContract, bnbBuybackContract, contract: xcoinContract, disconnectWallet } = useWeb3();
  const { balances, loadingBalances } = useWallet();
  const { userData, user } = useUser();
  const { refresh: getQuoteTokenPrice } = useTokenPrice();

  const [searchParams] = useSearchParams();
  const referralAddress = searchParams.get('ref');

  const [selectedCoinKey, setSelectedCoinKey] = useState(initialState.coin || 'XCO');
  const [tradeType, setTradeType] = useState(initialState.tradeType || 'Comprar'); // 'Comprar' or 'Vender'
  const [orderType, setOrderType] = useState(initialState.orderType || 'Orden de Mercado');

  // --- Payment Method State ---
  const [paymentMethod, setPaymentMethod] = useState('USDT'); // 'USDT' (Web3) or 'CLP' (Internal)

  // --- Web3 State ---
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [xcoBalance, setXcoBalance] = useState('0');
  const [bnbBalance, setBnbBalance] = useState('0'); // Native BNB (Gas)

  const [allowanceUSDT, setAllowanceUSDT] = useState('0'); // User -> Crowdsale (For Buying XCO)
  const [allowanceBNB, setAllowanceBNB] = useState('0');   // User -> BNB ATM (For Buying BNB)
  const [allowanceXCO, setAllowanceXCO] = useState('0');   // User -> Buyback (For Selling)
  const [buybackLiquidity, setBuybackLiquidity] = useState('0'); // Contract USDT Balance (XCO)
  const [bnbBuybackLiquidity, setBnbBuybackLiquidity] = useState('0'); // Contract USDT Balance (BNB)

  const [isApproving, setIsApproving] = useState(false);
  const [isBuying, setIsBuying] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [approvedForCurrentQuote, setApprovedForCurrentQuote] = useState(false);
  // --- Transaction Confirmation Modal State ---
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [gasStatus, setGasStatus] = useState('idle'); // 'calculating', 'ready', 'error'
  const [estimatedGasLimit, setEstimatedGasLimit] = useState(null);
  const [estimatedGasCost, setEstimatedGasCost] = useState(null);
  const [modalTitle, setModalTitle] = useState('Confirmar Transacción');
  const [modalActionLabel, setModalActionLabel] = useState('Continuar');
  const [modalError, setModalError] = useState(null);
  // --- Ref for Promise-based modal confirmation ---
  const confirmResolverRef = useRef(null);
  // --- Ref: Deferred success modal (shows after overlay 'Continuar') ---
  // ⚠️ DO NOT REMOVE (v4.3.7): This ref gates the success modal so it only appears
  //    AFTER the user presses 'Continuar' on the TransactionProgressOverlay.
  //    Without it, both overlay and modal render simultaneously (broken UX).
  const pendingSuccessModal = useRef(false);

  // --- Helper: Prepare Transaction (Estimate Gas) ---
  // Returns the safe gas limit if user confirms, or null if cancelled/error.
  // The entire flow stays in a single async chain to preserve WC session context.
  const prepareTransaction = async (actionType, estimateFn, executeFn, title, label) => {
    setShowConfirmModal(true);
    setGasStatus('calculating');
    setModalTitle(title);
    setModalActionLabel(label);
    setModalError(null);
    setEstimatedGasLimit(null);
    setEstimatedGasCost(null);

    // Initial Delay (UI feedback)
    await new Promise(r => setTimeout(r, 800));

    try {
      console.log(`[Flow] Estimating gas for ${actionType}...`);
      let estimatedGas;

      // Execute estimation function (returns BigInt)
      try {
        estimatedGas = await estimateFn();
      } catch (estError) {
        console.warn(`[Flow] Gas estimation failed for ${actionType}:`, estError);
        throw estError;
      }

      // Add Safety Margin (+20%)
      const safeGasLimit = (estimatedGas * 120n) / 100n;

      // Calculate Cost in BNB (Approx)
      let gasPrice = 3000000000n; // default 3 gwei
      try {
        const publicProvider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
        const feeData = await publicProvider.getFeeData();
        if (feeData.gasPrice) gasPrice = feeData.gasPrice;
      } catch (e) {
        console.warn("[Flow] Could not fetch gas price, using default 3 gwei");
      }

      const costWei = safeGasLimit * gasPrice;
      const costBNB = ethers.formatEther(costWei);
      const costFormatted = parseFloat(costBNB).toFixed(6) + ' BNB';

      setEstimatedGasLimit(safeGasLimit);
      setEstimatedGasCost(costFormatted);
      setGasStatus('ready');

      // Wait for user to click Confirm or Cancel (Promise-based)
      const confirmed = await new Promise(resolve => {
        confirmResolverRef.current = resolve;
      });

      setShowConfirmModal(false);
      confirmResolverRef.current = null;

      if (confirmed) {
        console.log(`[Flow] User confirmed ${actionType}, executing with gasLimit=${safeGasLimit}...`);
        await executeFn(safeGasLimit);
      } else {
        console.log(`[Flow] User cancelled ${actionType}.`);
      }
    } catch (err) {
      console.error(`[Flow] Error preparing ${actionType}:`, err);
      setGasStatus('error');
      let msg = err.reason || err.message;
      if (msg.includes("insufficient funds")) msg = "Fondos insuficientes para cubrir el gas.";
      setModalError(msg);
    }
  };

  // --- Helper: Resolve modal confirmation ---
  const executePending = () => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(true);
    }
  };

  const cancelPending = () => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(false);
      confirmResolverRef.current = null;
      setShowConfirmModal(false);
    }
  };


  // --- Spot Price / Quote State ---
  const [quote, setQuote] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isQuoting, setIsQuoting] = useState(false);
  const [showGasModal, setShowGasModal] = useState(false); // Education Modal State
  const [bnbLivePrice, setBnbLivePrice] = useState(null); // Real-time BNB price with spread
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [transactionData, setTransactionData] = useState(null);

  // --- Transaction Progress Overlay ---
  const [txPhase, setTxPhase] = useState('idle');
  const [txStep, setTxStep] = useState(1);
  const [txTotalSteps, setTxTotalSteps] = useState(1);
  const [txOverlayMsg, setTxOverlayMsg] = useState('');
  const [txOverlayHash, setTxOverlayHash] = useState(null);
  const [txOverlayError, setTxOverlayError] = useState(null);

  // --- Contract Ready State (Fix for undefined contract errors) ---
  const [contractsReady, setContractsReady] = useState(false);

  // --- Data Safety ---
  const selectedCoin = cryptoData[selectedCoinKey];
  const [quantity, setQuantity] = useState(initialState.quantity || 0.001);
  const [price, setPrice] = useState(initialState.price || selectedCoin?.price || 0);

  // --- NEW: User Control State ---
  const [hasManuallyEdited, setHasManuallyEdited] = useState(false);

  // --- Effect: Monitor Contract Initialization ---
  useEffect(() => {
    // Check if required contracts are initialized based on selected coin and trade type
    if (selectedCoinKey === 'BNB') {
      if (tradeType === 'Comprar') {
        setContractsReady(!!bnbAtmContract && !!usdtContract);
      } else {
        setContractsReady(!!bnbBuybackContract);
      }
    } else if (selectedCoinKey === 'XCO') {
      if (tradeType === 'Comprar') {
        setContractsReady(!!crowdsaleContract && !!usdtContract);
      } else {
        setContractsReady(!!buybackContract && !!xcoinContract);
      }
    } else {
      setContractsReady(true); // Other coins don't need contract validation yet
    }
  }, [selectedCoinKey, tradeType, bnbAtmContract, bnbBuybackContract, crowdsaleContract, buybackContract, usdtContract, xcoinContract]);

  // --- Request Notification Permission on Mount ---
  useEffect(() => { requestNotificationPermission(); }, []);

  // --- Transaction Overlay Helpers ---
  const showTxOverlay = (phase, step, totalSteps, msg, hash = null, error = null) => {
    setTxPhase(phase);
    setTxStep(step);
    setTxTotalSteps(totalSteps);
    setTxOverlayMsg(msg);
    if (hash !== null) setTxOverlayHash(hash);
    if (error !== null) setTxOverlayError(error);
  };
  const hideTxOverlay = () => {
    setTxPhase('idle');
    setTxOverlayHash(null);
    setTxOverlayError(null);
  };

  // --- Helper: Current Asset Balance ---
  const getCurrentAssetBalance = () => {
    if (!wallet) return 0;
    if (selectedCoinKey === 'BNB') return parseFloat(bnbBalance) || 0;
    if (selectedCoinKey === 'XCO') return parseFloat(xcoBalance) || 0;
    return balances ? (balances[selectedCoinKey] || 0) : 0;
  };

  const getPaymentBalance = () => {
    if (paymentMethod === 'CLP') return balances?.CLP || 0;
    return parseFloat(usdtBalance) || 0;
  };

  // --- Effect: Auto-Fill Max Amount ---
  useEffect(() => {
    // GUARD: Do NOT auto-fill if user has edited manually, if there's an active quote, 
    // or if a transaction is in progress (isApproving/isBuying).
    if (hasManuallyEdited || !!quote || isApproving || isBuying) return;

    if (tradeType === 'Vender') {
      const bal = getCurrentAssetBalance();
      if (bal > 0) setQuantity(bal);
      else setQuantity(0);
    } else {
      // Comprar
      const payBal = getPaymentBalance();
      if (payBal > 0) {
        if (selectedCoinKey === 'XCO') {
          if (paymentMethod === 'CLP') {
            setQuantity(1);
          } else {
            // Permitir decimales para XCO (ej: 7 USDT -> 0.7 XCO)
            const maxBuy = payBal / 10;
            setQuantity(maxBuy);
          }
        } else if (selectedCoinKey === 'BNB') {
          // Predeterminar 0.001 (mínimo) para compra de BNB
          setQuantity(0.001);
        } else {
          setQuantity(1);
        }
      } else {
        setQuantity(0);
      }
    }
  }, [selectedCoinKey, tradeType, paymentMethod, bnbLivePrice, balances, usdtBalance, xcoBalance, bnbBalance, hasManuallyEdited, quote, isApproving, isBuying]);

  // --- Fetch BNB Live Price (Real-time with Dynamic Spread) ---
  useEffect(() => {
    const fetchBNBPrice = async () => {
      if (selectedCoinKey === 'BNB') {
        try {
          // Use wallet address if available, otherwise use dummy for price display
          const userAddr = wallet?.accounts?.[0]?.address || "0x0000000000000000000000000000000000000000";
          const response = await fetch(`/api-bnb/quote`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amountUSDT: 1,
              userAddress: userAddr,
              tradeType: tradeType
            })
          });
          const data = await response.json();
          if (data.success) {
            setBnbLivePrice(data.data.quotePrice);
          }
        } catch (error) {
          console.error('Error fetching BNB live price:', error);
        }
      } else {
        setBnbLivePrice(null);
      }
    };

    fetchBNBPrice();
    const interval = setInterval(fetchBNBPrice, 30000);
    return () => clearInterval(interval);
  }, [selectedCoinKey, tradeType, wallet]);

  // --- Fetch BNB ATM Allowance (For Buying BNB with USDT) ---
  useEffect(() => {
    const fetchBNBAllowance = async () => {
      if (wallet && usdtContract && bnbAtmContract && selectedCoinKey === 'BNB') {
        try {
          const userAddr = wallet?.accounts?.[0]?.address;
          if (!userAddr) return;

          // Get USDT balance
          const uBal = await usdtContract.balanceOf(userAddr);
          setUsdtBalance(ethers.formatUnits(uBal, 18));

          // Get allowance for BNB ATM contract
          const bnbAtmAddr = await bnbAtmContract.getAddress();
          const allowBNB = await usdtContract.allowance(userAddr, bnbAtmAddr);
          setAllowanceBNB(ethers.formatUnits(allowBNB, 18));

          // Get native BNB balance (for gas) - independent try-catch with public RPC fallback
          try {
            if (wallet.provider) {
              const provider = new HybridBrowserProvider(wallet.provider);
              const bnbBal = await provider.getBalance(userAddr);
              setBnbBalance(ethers.formatUnits(bnbBal, 18));
            }
          } catch (bnbGasErr) {
            console.warn('BNB gas balance via wallet failed, using public RPC:', bnbGasErr.message);
            try {
              const publicProvider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
              const bnbBal = await publicProvider.getBalance(userAddr);
              setBnbBalance(ethers.formatUnits(bnbBal, 18));
            } catch (e) { console.error('BNB gas fallback also failed:', e.message); }
          }
        } catch (err) {
          console.error('Error fetching BNB allowance:', err);
        }
      }
    };

    fetchBNBAllowance();
    const interval = setInterval(fetchBNBAllowance, 5000);
    return () => clearInterval(interval);
  }, [wallet, usdtContract, bnbAtmContract, selectedCoinKey]);

  // --- Fetch Web3 Data (Balance & Allowance) ---
  useEffect(() => {
    const fetchData = async () => {
      // General Requirements: Wallet connected & Asset selected
      if (wallet && xcoinContract && (selectedCoinKey === 'XCO' || selectedCoinKey === 'BNB')) {
        const userAddr = wallet?.accounts?.[0]?.address;
        if (!userAddr) return;

        // INDEPENDENT: Native BNB Balance (Gas) - ALWAYS fetch, even if contract calls fail
        try {
          if (wallet.provider) {
            const gasProvider = new HybridBrowserProvider(wallet.provider);
            const bnbGas = await gasProvider.getBalance(userAddr);
            setBnbBalance(ethers.formatUnits(bnbGas, 18));
          }
        } catch (bnbGasErr) {
          console.warn('BNB gas balance via wallet failed, using public RPC:', bnbGasErr.message);
          try {
            const publicProvider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            const bnbGas = await publicProvider.getBalance(userAddr);
            setBnbBalance(ethers.formatUnits(bnbGas, 18));
          } catch (e) { console.error('BNB gas fallback also failed:', e.message); }
        }

        // Contract-dependent data (may fail if session is stale)
        try {
          // A. User Balances
          const xBal = await xcoinContract.balanceOf(userAddr);
          setXcoBalance(ethers.formatUnits(xBal, 18));

          // Buying Requirements (USDT + Crowdsale)
          if (usdtContract && crowdsaleContract) {
            const uBal = await usdtContract.balanceOf(userAddr);
            setUsdtBalance(ethers.formatUnits(uBal, 18));

            const csAddr = await crowdsaleContract.getAddress();
            const allowU = await usdtContract.allowance(userAddr, csAddr);
            setAllowanceUSDT(ethers.formatUnits(allowU, 18));
          }

          // Selling Requirements (Buyback Contract)
          if (buybackContract) {
            const bbAddr = await buybackContract.getAddress();

            // User Allowance to Buyback Contract
            const allowX = await xcoinContract.allowance(userAddr, bbAddr);
            setAllowanceXCO(ethers.formatUnits(allowX, 18));

            // Check Buyback Contract Liquidity (How much USDT it has)
            if (usdtContract) {
              const bbLiq = await usdtContract.balanceOf(bbAddr);
              setBuybackLiquidity(ethers.formatUnits(bbLiq, 18));
            }
          }

          // D. BNB Buyback Liquidity (siempre leer cuando contratos disponibles)
          if (bnbBuybackContract && usdtContract) {
            try {
              const bnbBbAddr = await bnbBuybackContract.getAddress();
              const bnbLiq = await usdtContract.balanceOf(bnbBbAddr);
              setBnbBuybackLiquidity(ethers.formatUnits(bnbLiq, 18));
            } catch (bnbErr) {
              console.error("Error reading BNB liquidity:", bnbErr);
            }
          }
        } catch (err) {
          console.error("Error fetching Web3 data:", err);
        }
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 5000); // Poll every 5s
    return () => clearInterval(interval);
  }, [wallet, usdtContract, crowdsaleContract, buybackContract, bnbBuybackContract, xcoinContract, selectedCoinKey, paymentMethod]);

  useEffect(() => {
    if (selectedCoin) {
      const newPrice = orderType === 'Orden de Mercado' ? (selectedCoinKey === 'XCO' ? 10 : selectedCoin.price) : 'Automático';
      setPrice(newPrice);
    }
  }, [orderType, selectedCoin]);

  // RESET manual edit flag ONLY when coin key or trade type changes
  useEffect(() => {
    setHasManuallyEdited(false);
  }, [selectedCoinKey, tradeType]);

  // --- Countdown Timer Effect ---
  useEffect(() => {
    let interval = null;
    if (quote && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setQuote(null); // Expire
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timeLeft === 0) {
      setQuote(null);
    }
    return () => clearInterval(interval);
  }, [quote, timeLeft]);


  // --- Handlers ---



  const handleGetBNBQuote = async () => {
    setIsQuoting(true);
    setApprovedForCurrentQuote(false);
    setStatusMsg('Cotizando BNB en servidor (Oracle)...');
    try {
      const userAddr = wallet?.accounts?.[0]?.address;
      if (!userAddr) throw new Error("Wallet no conectada");

      const response = await fetch(`/api-bnb/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amountBNB: quantity, userAddress: userAddr, currency: 'USDT' })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Error al obtener cotización");

      setQuote({
        ...data.data, // Contains signature, expiry, etc.
        bnbAmount: data.data.amountBNB,
        xcoClp: 0, // Not used for BNB
        exchangeRate: data.data.bnbPrice // Display price
      });
      setTimeLeft(300); // 5 minutes (from backend logic)
      setStatusMsg('');
    } catch (e) {
      console.error(e);
      setStatusMsg('Error al cotizar BNB: ' + e.message);
    } finally {
      setIsQuoting(false);
    }
  };
  const handleGetBNBSellQuote = async () => {
    setIsQuoting(true);
    setStatusMsg('Cotizando venta de BNB...');
    try {
      const userAddr = wallet?.accounts?.[0]?.address;
      if (!userAddr) throw new Error("Wallet no conectada");

      const response = await fetch(`/api-bnb/sell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amountBNB: quantity, userAddress: userAddr })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || "Error al obtener cotización de venta");

      // Obtener precio USDT/CLP para conversión
      const usdtClpPrice = await getQuoteTokenPrice();
      if (!usdtClpPrice) throw new Error("No se pudo obtener precio USDT/CLP");

      // Calcular total en CLP
      const paymentUSDT = parseFloat(data.data.paymentUSDT);
      const totalClp = paymentUSDT * usdtClpPrice;

      setQuote({
        ...data.data,
        clpTotal: totalClp,
        exchangeRate: data.data.bnbPrice,
        paymentUSDT: paymentUSDT
      });
      setTimeLeft(300);
      setStatusMsg('');
    } catch (e) {
      console.error(e);
      setStatusMsg('Error al cotizar venta BNB: ' + e.message);
    } finally {
      setIsQuoting(false);
    }
  };



  const handleGetQuote = async () => {
    if (selectedCoinKey === 'BNB') {
      if (tradeType === 'Comprar' && paymentMethod === 'USDT') {
        await handleGetBNBQuote();
        return;
      }
      if (tradeType === 'Vender') {
        await handleGetBNBSellQuote();
        return;
      }
    }

    setIsQuoting(true);
    setApprovedForCurrentQuote(false);
    setStatusMsg('Obteniendo cotización...');
    try {
      // 1. Get Exchange Rate (USDT -> CLP)
      const usdtClpPrice = await getQuoteTokenPrice(); // e.g. 950
      if (!usdtClpPrice) throw new Error("No se pudo obtener precio USDT/CLP");

      let totalClp = 0;
      let amountCrypto = 0;
      let finalExchRate = usdtClpPrice; // Default for XCO (10 USDT fixed)

      if (selectedCoinKey === 'BNB') {
        const oracleRes = await fetch(`/api-bnb/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amountBNB: quantity, userAddress: "0x0000000000000000000000000000000000000000" }) // Dummy addr for price
        });
        const oracleData = await oracleRes.json();
        if (!oracleData.success) throw new Error("Error obteniendo precio BNB");

        const bnbPriceUSDT = oracleData.data.bnbPrice;
        amountCrypto = quantity;
        totalClp = oracleData.data.costUSDT * usdtClpPrice;
        finalExchRate = bnbPriceUSDT; // To show BNB Price

        setQuote({
          clpTotal: totalClp,
          exchangeRate: finalExchRate,
          bnbAmount: amountCrypto,
          costUSDT: oracleData.data.costUSDT,
          isCLP: true
        });
      } else {
        // XCO Logic
        const xcoPriceInClp = usdtClpPrice * 10;
        totalClp = xcoPriceInClp * quantity; // Quantity Input is XCO
        amountCrypto = quantity;
        finalExchRate = usdtClpPrice;

        setQuote({
          clpTotal: totalClp,
          exchangeRate: finalExchRate,
          bnbAmount: amountCrypto,
          costUSDT: quantity * 10,
          isCLP: true
        });
      }
      setTimeLeft(QUOTE_TIMEOUT);
      setStatusMsg('');
    } catch (e) {
      console.error(e);
      setStatusMsg('Error al cotizar: ' + e.message);
    } finally {
      setIsQuoting(false);
    }
  };


  const handleApprove = async () => {
    setIsApproving(true);
    setStatusMsg(tradeType === 'Comprar' ? 'Iniciando aprobación...' : 'Iniciando aprobación XCO...');

    try {
      // Pre-validate chain
      await validateChainBeforeTx(wallet);

      const tokenLabel = tradeType === 'Comprar' ? 'USDT' : 'XCO';
      let tokenContract = usdtContract;
      let spender = null;
      let localQuantity = quantity;

      // Determine Configuration based on Trade Type
      if (tradeType === 'Comprar') {
        if (selectedCoinKey === 'XCO') {
          if (!crowdsaleContract) return;
          spender = await crowdsaleContract.getAddress();
          localQuantity = quantity * 10;
        } else if (selectedCoinKey === 'BNB') {
          if (!bnbAtmContract || !quote) return;
          spender = await bnbAtmContract.getAddress();
          localQuantity = quote.costUSDT;
        }
      } else {
        tokenContract = xcoinContract;
        if (!xcoinContract || !buybackContract) return;
        spender = await buybackContract.getAddress();
        localQuantity = quantity;
      }

      if (!spender) return;

      const amountToApproveWei = ethers.parseUnits(localQuantity.toString(), 18);
      const userAddr = wallet.accounts[0].address;

      // 1. Check Existing Allowance (Guard)
      const currentAllowance = await tokenContract.allowance(userAddr, spender);
      if (currentAllowance >= amountToApproveWei) {
        console.log('[Approve] Allowance sufficient, skipping transaction.');
        if (selectedCoinKey === 'BNB') setAllowanceBNB(ethers.formatUnits(currentAllowance, 18));
        else if (tokenLabel === 'USDT') setAllowanceUSDT(ethers.formatUnits(currentAllowance, 18));
        else setAllowanceXCO(ethers.formatUnits(currentAllowance, 18));

        setApprovedForCurrentQuote(true);
        setStatusMsg('¡Aprobado! Ahora puedes proceder.');
        setIsApproving(false);
        return;
      }

      // 2. Pre-compute token address and approve data (local, no RPC)
      const tokenAddress = await tokenContract.getAddress();
      const approveData = tokenContract.interface.encodeFunctionData('approve', [spender, amountToApproveWei]);

      // 3. Define Execution Logic (EIP-1193 direct — bypasses ethers.js/HybridBrowserProvider)
      // ⚠️ CRITICAL (v4.3.7, 2026-02-12): DO NOT replace wallet.provider.request() with
      //    ethers.js contract.approve() or signer.sendTransaction(). Those break on Android
      //    WalletConnect with UNKNOWN_ERROR / "Failed to publish payload" / error 5300.
      //    The EIP-1193 direct pattern is the ONLY reliable way to send approve transactions
      //    on mobile WalletConnect. Gas estimation also uses public RPC directly.
      //    See: handleSellBNBToCLP for the same proven pattern.
      const executeApprove = async (gasLimit) => {
        setIsApproving(true);
        setStatusMsg(`Aprobando ${tokenLabel}...`);
        showTxOverlay('waiting-wallet', 1, 2, `Abre tu wallet y aprueba el gasto de ${tokenLabel}...`);

        try {
          // Send approve via EIP-1193 directly (same pattern as TX8 handleSellBNBToCLP)
          const txHash = await wallet.provider.request({
            method: 'eth_sendTransaction',
            params: [{
              from: userAddr,
              to: tokenAddress,
              data: approveData,
              gas: ethers.toQuantity(gasLimit)
            }]
          });

          showTxOverlay('mining', 1, 2, 'Aprobación enviada. Esperando confirmación...', txHash);
          setStatusMsg('Esperando confirmación de la red...');

          // Wait for confirmation using public RPC (reliable, no WC dependency)
          const publicProvider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
          const receipt = await withTimeout(
            publicProvider.waitForTransaction(txHash, 1),
            TX_TIMEOUT,
            'Tiempo de espera agotado esperando confirmación.'
          );
          if (!receipt || receipt.status !== 1) {
            throw new Error("Transacción de aprobación falló en blockchain");
          }

          // Refresh Allowance State
          const allow = await tokenContract.allowance(userAddr, spender);
          if (selectedCoinKey === 'BNB') setAllowanceBNB(ethers.formatUnits(allow, 18));
          else if (tokenLabel === 'USDT') setAllowanceUSDT(ethers.formatUnits(allow, 18));
          else setAllowanceXCO(ethers.formatUnits(allow, 18));

          setApprovedForCurrentQuote(true);
          setStatusMsg('¡Aprobado! Ahora puedes proceder.');
          showTxOverlay('confirmed', 1, 2, '¡Aprobación confirmada!');
          sendTxNotification('✅ Aprobación Exitosa', `Has aprobado el uso de ${tokenLabel}.`);
          setTimeout(() => hideTxOverlay(), 3000);

        } catch (err) {
          console.error(err);
          handleTxError(err, 'Aprobación fallida');
        } finally {
          setIsApproving(false);
        }
      };

      // 4. Prepare Transaction (Estimate Gas via Public RPC -> Modal -> Execute)
      const estimateFn = async () => {
        const publicProvider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
        return await publicProvider.estimateGas({
          from: userAddr,
          to: tokenAddress,
          data: approveData
        });
      };

      await prepareTransaction(
        `Aprobar ${tokenLabel}`,
        estimateFn,
        executeApprove,
        `Confirmar Aprobación ${tokenLabel}`,
        `Aprobar ${tokenLabel}`
      );

    } catch (err) {
      console.error(err);
      handleTxError(err, 'Error inicializando aprobación');
      setIsApproving(false);
    }
  };

  // Helper for error handling to reduce duplication
  // ⚠️ CRITICAL (v4.3.8): Uses current txStep/txTotalSteps instead of hardcoded 1,2.
  //    Previously hardcoded step=1 caused misleading error overlay (showed step 1 error when step 2 failed).
  const handleTxError = (err, title) => {
    const step = txStep || 1;
    const total = txTotalSteps || 2;
    if (isWalletConnectSessionError(err)) {
      console.warn('[WC] Session error. Disconnecting...');
      try { disconnectWallet(); } catch (_) { }
      const msg = 'Sesión expirada. Vuelve a conectar tu wallet.';
      setStatusMsg(msg);
      showTxOverlay('error', step, total, title, null, msg);
    } else {
      const msg = err.reason || err.message;
      setStatusMsg('Error: ' + msg);
      showTxOverlay('error', step, total, title, null, msg);
    }
  };

  // Helper for balance change detection
  const checkBalanceChanged = async (userAddr, contract, tokenSymbol, increaseExpected) => {
    const currentBalance = parseFloat(ethers.formatUnits(await contract.balanceOf(userAddr), 18));
    const initialBalance = parseFloat(tokenBalances[tokenSymbol]); // Assuming tokenBalances is available and up-to-date
    if (increaseExpected) {
      return currentBalance > initialBalance;
    } else {
      return currentBalance < initialBalance;
    }
  };

  const handleTransact = async () => {
    setIsBuying(true);
    setStatusMsg(tradeType === 'Comprar' ? 'Preparando compra...' : 'Preparando venta...');
    const txLabel = tradeType === 'Comprar' ? `Compra de ${selectedCoinKey}` : `Venta de ${selectedCoinKey}`;

    //showTxOverlay('waiting-wallet', 2, 2, `Confirma la ${txLabel} en tu wallet...`);
    // NOTE: Overlay is now managed by executeTransact -> after modal

    try {
      await validateChainBeforeTx(wallet);

      // --- 1. Prepare Variables ---
      let estimateFn = null;
      let executeFn = null;
      let txTitle = tradeType === 'Comprar' ? `Compra ${selectedCoinKey}` : `Venta ${selectedCoinKey}`;
      let modalLabel = tradeType === 'Comprar' ? `Comprar ${selectedCoinKey}` : `Vender ${selectedCoinKey}`;

      if (tradeType === 'Comprar') {
        if (selectedCoinKey === 'BNB') {
          // BUY BNB (USDT -> BNB)
          // ⚠️ CRITICAL (v4.3.8): EIP-1193 direct pattern — DO NOT revert to ethers.js contract call.
          //    ethers.js + HybridBrowserProvider hangs on Android WalletConnect when switching apps.
          if (!bnbAtmContract || !quote) return;
          const { amountBNBWei, costUSDTWei, expiry, signature } = quote;
          const bnbAtmAddr = await bnbAtmContract.getAddress();
          const buyBNBData = bnbAtmContract.interface.encodeFunctionData('buyBNB', [amountBNBWei, costUSDTWei, expiry, signature]);

          estimateFn = async () => {
            const pub = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            return await pub.estimateGas({ from: wallet.accounts[0].address, to: bnbAtmAddr, data: buyBNBData });
          };

          executeFn = async (gasLimit) => {
            showTxOverlay('waiting-wallet', 2, 2, `Confirma la Compra de BNB en tu wallet...`);
            const txHash = await wallet.provider.request({
              method: 'eth_sendTransaction',
              params: [{ from: wallet.accounts[0].address, to: bnbAtmAddr, data: buyBNBData, gas: ethers.toQuantity(gasLimit) }]
            });
            showTxOverlay('mining', 2, 2, 'Transacción enviada. Esperando confirmación...', txHash);
            const pub = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            const receipt = await withTimeout(pub.waitForTransaction(txHash, 1), TX_TIMEOUT, 'Tiempo de espera agotado esperando confirmación de la red.');
            if (!receipt || receipt.status !== 1) throw new Error('Transacción falló en blockchain');
            return { result: { source: 'eip1193', tx: { hash: txHash }, receipt }, logType: 'bnb' };
          };

        } else {
          // BUY XCO (USDT -> XCO)
          if (!crowdsaleContract) return;

          // Referral Logic
          let finalReferrer = '0x0000000000000000000000000000000000000000';
          const params = new URLSearchParams(window.location.search);
          const urlRef = params.get('ref');

          // Check DB User Data
          if (userData && userData.referredBy && userData.referredBy !== '0x0000000000000000000000000000000000000000') {
            finalReferrer = userData.referredBy;
          } else if (urlRef && ethers.isAddress(urlRef)) {
            finalReferrer = urlRef;
          }

          // Anti-Self-Referral & Admin Guards
          const userMetaMask = wallet.accounts[0].address;
          const crowdsaleAddr = await crowdsaleContract.getAddress();
          const ownerAddr = await crowdsaleContract.owner();

          if (finalReferrer.toLowerCase() === userMetaMask.toLowerCase()) finalReferrer = '0x0000000000000000000000000000000000000000';
          if (finalReferrer.toLowerCase() === crowdsaleAddr.toLowerCase()) finalReferrer = '0x0000000000000000000000000000000000000000';
          if (finalReferrer.toLowerCase() === ownerAddr.toLowerCase()) finalReferrer = '0x0000000000000000000000000000000000000000';

          const usdtCostWei = ethers.parseUnits((quantity * 10).toString(), 18);
          const hasRef = (finalReferrer !== '0x0000000000000000000000000000000000000000');

          // ⚠️ CRITICAL (v4.3.8): EIP-1193 direct pattern — DO NOT revert to ethers.js contract call.
          const buyXCOData = hasRef
            ? crowdsaleContract.interface.encodeFunctionData('buyWithReferral', [usdtCostWei, finalReferrer])
            : crowdsaleContract.interface.encodeFunctionData('buyXCoin', [usdtCostWei]);

          estimateFn = async () => {
            const pub = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            return await pub.estimateGas({ from: wallet.accounts[0].address, to: crowdsaleAddr, data: buyXCOData });
          };

          executeFn = async (gasLimit) => {
            showTxOverlay('waiting-wallet', 2, 2, `Confirma la Compra de XCO en tu wallet...`);
            const txHash = await wallet.provider.request({
              method: 'eth_sendTransaction',
              params: [{ from: wallet.accounts[0].address, to: crowdsaleAddr, data: buyXCOData, gas: ethers.toQuantity(gasLimit) }]
            });
            showTxOverlay('mining', 2, 2, 'Transacción enviada. Esperando confirmación...', txHash);
            const pub = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            const receipt = await withTimeout(pub.waitForTransaction(txHash, 1), TX_TIMEOUT, 'Tiempo de espera agotado esperando confirmación de la red.');
            if (!receipt || receipt.status !== 1) throw new Error('Transacción falló en blockchain');
            return { result: { source: 'eip1193', tx: { hash: txHash }, receipt }, logType: 'xco', referrer: finalReferrer };
          };
        }

      } else {
        // SELL
        if (selectedCoinKey === 'BNB') {
          // SELL BNB (BNB -> USDT)
          // ⚠️ CRITICAL (v4.3.8): EIP-1193 direct pattern — DO NOT revert to ethers.js contract call.
          //    sellBNB is payable, needs value parameter in eth_sendTransaction.
          if (!bnbBuybackContract || !quote) return;
          const { amountBNBWei, paymentUSDTWei, expiry, signature } = quote;
          const bnbBuybackAddr = await bnbBuybackContract.getAddress();
          const sellBNBData = bnbBuybackContract.interface.encodeFunctionData('sellBNB', [amountBNBWei, paymentUSDTWei, expiry, signature]);

          estimateFn = async () => {
            const pub = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            return await pub.estimateGas({ from: wallet.accounts[0].address, to: bnbBuybackAddr, data: sellBNBData, value: amountBNBWei });
          };

          executeFn = async (gasLimit) => {
            showTxOverlay('waiting-wallet', 2, 2, `Confirma la Venta de BNB en tu wallet...`);
            const txHash = await wallet.provider.request({
              method: 'eth_sendTransaction',
              params: [{ from: wallet.accounts[0].address, to: bnbBuybackAddr, data: sellBNBData, value: ethers.toQuantity(amountBNBWei), gas: ethers.toQuantity(gasLimit) }]
            });
            showTxOverlay('mining', 2, 2, 'Transacción enviada. Esperando confirmación...', txHash);
            const pub = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            const receipt = await withTimeout(pub.waitForTransaction(txHash, 1), TX_TIMEOUT, 'Tiempo de espera agotado esperando confirmación de la red.');
            if (!receipt || receipt.status !== 1) throw new Error('Transacción falló en blockchain');
            return { result: { source: 'eip1193', tx: { hash: txHash }, receipt }, logType: 'bnb_sell' };
          };

        } else {
          // SELL XCO (XCO -> USDT)
          // ⚠️ CRITICAL (v4.3.8): EIP-1193 direct pattern — DO NOT revert to ethers.js contract call.
          if (!buybackContract) return;
          const xcoAmountWei = ethers.parseUnits(quantity.toString(), 18);
          const buybackAddr = await buybackContract.getAddress();
          const sellXCOData = buybackContract.interface.encodeFunctionData('sellXCoin', [xcoAmountWei]);

          estimateFn = async () => {
            const pub = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            return await pub.estimateGas({ from: wallet.accounts[0].address, to: buybackAddr, data: sellXCOData });
          };

          executeFn = async (gasLimit) => {
            showTxOverlay('waiting-wallet', 2, 2, `Confirma la Venta de XCO en tu wallet...`);
            const txHash = await wallet.provider.request({
              method: 'eth_sendTransaction',
              params: [{ from: wallet.accounts[0].address, to: buybackAddr, data: sellXCOData, gas: ethers.toQuantity(gasLimit) }]
            });
            showTxOverlay('mining', 2, 2, 'Transacción enviada. Esperando confirmación...', txHash);
            const pub = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
            const receipt = await withTimeout(pub.waitForTransaction(txHash, 1), TX_TIMEOUT, 'Tiempo de espera agotado esperando confirmación de la red.');
            if (!receipt || receipt.status !== 1) throw new Error('Transacción falló en blockchain');
            return { result: { source: 'eip1193', tx: { hash: txHash }, receipt }, logType: 'xco_sell' };
          };
        }
      }

      // --- 2. Define High-Level Execution Wrapper ---
      const executeTransact = async (gasLimit) => {
        setIsBuying(true);
        setStatusMsg(`Procesando ${txLabel}...`);

        try {
          const { result, logType, referrer } = await executeFn(gasLimit);
          // Handle Result (must await — async function)
          await handleTxSuccess(result, logType, referrer);
        } catch (err) {
          handleTxError(err, `${txLabel} fallida`);
        } finally {
          setIsBuying(false);
        }
      };

      // --- 3. Run Preparation Flow ---
      await prepareTransaction(
        txTitle,
        estimateFn,
        executeTransact,
        `Confirmar ${txTitle}`,
        modalLabel
      );


    } catch (err) {
      console.error(err);
      handleTxError(err, `${txLabel} fallida`);
      setIsBuying(false);
    }
  };

  // Helper: Log transaction to Firestore + SQL (non-blocking, called after success modal)
  // ⚠️ CRITICAL (v4.3.7): This function was extracted from handleTxSuccess to ensure
  //    logging errors NEVER block the success modal. It is called wrapped in try-catch.
  //    DO NOT inline this back into handleTxSuccess before setShowSuccessModal/pendingSuccessModal.
  // ⚠️ v4.4.0: Migrated from Firestore to VPS/SQL (PostgreSQL). Zero Firebase writes.
  const logTransaction = async (txHash, logType, referrer) => {
    if (!user) return;
    const LOG_API_URL = import.meta.env.VITE_API_URL || 'https://dashboard.xcoin.cl';
    const timestamp = Date.now();

    // 1. Register in transacciones table (PostgreSQL) — replaces Firestore
    if (logType === 'xco') {
      await fetch(`${LOG_API_URL}/api/transaccion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          tipo: 'compra',
          moneda: 'XCO',
          cantidad: Number(quantity),
          montoUSDT: Number(quote?.costUSDT || 0),
          txHash: txHash,
          method: 'USDT',
          description: 'Compra XCO',
          origen: 'Frontend'
        })
      });
      console.log("[logTransaction] Compra XCO registrada en PostgreSQL:", txHash);

    } else if (logType === 'xco_sell') {
      await fetch(`${LOG_API_URL}/api/transaccion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          tipo: 'venta',
          moneda: 'XCO',
          cantidad: Number(quantity),
          montoUSDT: Number(quote?.paymentUSDT || ethers.formatUnits(quote?.paymentUSDTWei || '0', 18)),
          txHash: txHash,
          method: 'USDT',
          description: 'Venta XCO',
          origen: 'Frontend'
        })
      });
      console.log("[logTransaction] Venta XCO registrada en PostgreSQL:", txHash);

    } else if (logType === 'bnb' || logType === 'bnb_sell') {
      const isBuy = (logType === 'bnb');

      // A. Register in transacciones table (user history)
      await fetch(`${LOG_API_URL}/api/transaccion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          tipo: isBuy ? 'compra' : 'venta',
          moneda: 'BNB',
          cantidad: Number(quote?.bnbAmount || 0),
          montoUSDT: isBuy
            ? Number(quote?.costUSDT || 0)
            : Number(ethers.formatUnits(quote?.paymentUSDTWei || '0', 18)),
          txHash: txHash,
          method: 'USDT',
          description: isBuy ? 'Compra BNB' : 'Venta BNB',
          origen: 'Frontend'
        })
      });

      // B. Register in cajero movimientos (BNB + USDT dual entry)
      if (isBuy) {
        // 1. Historial BNB: Cajero VENDE BNB (tipo='venta', rojo, resta)
        await fetch(`${LOG_API_URL}/api-bnb/movimiento`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cajero: 'venta',
            tipo: 'venta',
            bnbAmount: parseFloat(quote?.bnbAmount || 0),
            usdtAmount: parseFloat(quote?.costUSDT || 0),
            userAddress: wallet.accounts[0].address,
            txHash: txHash,
            timestamp: timestamp,
            contractAddress: '0x6DeF104b6542d323Ef6a2Bebad60309E85196d95'
          })
        });
        // 2. Historial USDT: Cajero RECAUDA USDT (tipo='compra', verde, suma)
        await fetch(`${LOG_API_URL}/api-bnb/movimiento`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cajero: 'compra',
            tipo: 'compra',
            bnbAmount: parseFloat(quote?.bnbAmount || 0),
            usdtAmount: parseFloat(quote?.costUSDT || 0),
            userAddress: wallet.accounts[0].address,
            txHash: txHash,
            timestamp: timestamp,
            contractAddress: '0x6DeF104b6542d323Ef6a2Bebad60309E85196d95'
          })
        });
        console.log("[logTransaction] Compra BNB registrada en PostgreSQL (transacciones + cajero)");
      } else {
        // 1. Historial BNB: Cajero COMPRA BNB (tipo='compra', verde, suma)
        await fetch(`${LOG_API_URL}/api-bnb/movimiento`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cajero: 'venta',
            tipo: 'compra',
            bnbAmount: parseFloat(quote?.bnbAmount || 0),
            usdtAmount: parseFloat(ethers.formatUnits(quote?.paymentUSDTWei || '0', 18) || 0),
            userAddress: wallet.accounts[0].address,
            txHash: txHash,
            timestamp: timestamp,
            contractAddress: '0x6DeF104b6542d323Ef6a2Bebad60309E85196d95'
          })
        });
        // 2. Historial USDT: Cajero PAGA USDT (tipo='venta', rojo, resta)
        await fetch(`${LOG_API_URL}/api-bnb/movimiento`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cajero: 'compra',
            tipo: 'venta',
            bnbAmount: parseFloat(quote?.bnbAmount || 0),
            usdtAmount: parseFloat(ethers.formatUnits(quote?.paymentUSDTWei || '0', 18) || 0),
            userAddress: wallet.accounts[0].address,
            txHash: txHash,
            timestamp: timestamp,
            contractAddress: '0x6DeF104b6542d323Ef6a2Bebad60309E85196d95'
          })
        });
        console.log("[logTransaction] Venta BNB registrada en PostgreSQL (transacciones + cajero)");
      }
    }
  };

  // ⚠️ CRITICAL (v4.3.7, 2026-02-12): handleTxSuccess UX Flow
  // ─────────────────────────────────────────────────────────────
  // This function is called from executeTransact and MUST be awaited.
  // The flow is:
  //   1. showTxOverlay('confirmed') → user sees "¡Transacción Confirmada!" + "Continuar" button
  //   2. pendingSuccessModal.current = true → DEFERS the success modal (does NOT show it immediately)
  //   3. User presses "Continuar" → onDismiss fires → hideTxOverlay() + setShowSuccessModal(true)
  //   4. User presses "Cerrar" on success modal → widget is already reset
  //
  // DO NOT change pendingSuccessModal.current back to setShowSuccessModal(true) directly.
  // That causes both the overlay AND the success modal to render simultaneously,
  // which was the bug before v4.3.7.
  //
  // Logging (logTransaction) runs AFTER the modal data is set, wrapped in try-catch.
  // A logging failure must NEVER prevent the success modal from appearing.
  // ─────────────────────────────────────────────────────────────
  const handleTxSuccess = async (result, logType, referrer) => {
    let txHash = result.tx ? result.tx.hash : 'unknown';
    if (result.receipt) txHash = result.receipt.hash;

    if (result.source === 'wc') {
      showTxOverlay('mining', 2, 2, 'Transacción enviada. Esperando confirmación...', txHash);
      await withTimeout(result.tx.wait(), TX_TIMEOUT, 'Tiempo de espera agotado.');
    }

    showTxOverlay('confirmed', 2, 2, '¡Transacción Exitosa!');
    sendTxNotification('✅ Transacción Exitosa', `Tu operación de ${tradeType} se ha completado.`);

    // 2. Show Success Modal FIRST (before logging — logging must never block user feedback)
    const gasCostBNB = result.receipt ? parseFloat(ethers.formatEther(result.receipt.gasUsed * (result.receipt.gasPrice || result.receipt.effectiveGasPrice))) : 0;
    const bnbPriceUSD = bnbLivePrice || 600; // Fallback price in USD
    const gasCostCLP = gasCostBNB * bnbPriceUSD * 950; // Convert BNB -> USD -> CLP

    const paymentAmount = selectedCoinKey === 'BNB'
      ? (tradeType === 'Comprar' ? (quote?.costUSDT || 0) : (quote?.paymentUSDT || 0))
      : (quantity * 10);
    setTransactionData({
      type: tradeType === 'Comprar' ? 'compra' : 'venta',
      crypto: selectedCoinKey,
      amount: quantity,
      payment: {
        amount: paymentAmount,
        currency: 'USDT'
      },
      gas: {
        bnb: gasCostBNB,
        clp: gasCostCLP
      },
      txHash: txHash,
      timestamp: Date.now(),
      success: true
    });
    pendingSuccessModal.current = true; // Will show when user presses 'Continuar' on overlay

    // 3. Refresh Balances & Reset UI
    if (typeof refreshBalances === 'function') refreshBalances();
    setQuantity(0);
    setQuote(null);
    setAllowanceUSDT('0');
    setAllowanceBNB('0');
    setAllowanceXCO('0');
    setApprovedForCurrentQuote(false);

    // 4. Logging (wrapped in try-catch — must never block success modal)
    try {
      await logTransaction(txHash, logType, referrer);
    } catch (logErr) {
      console.error('[Logging] Error registering transaction (non-blocking):', logErr);
    }
  };

  const handleSellToCLP = async () => {
    if (!quote) return;

    // 1. Validate XCO Balance
    const xcoBal = parseFloat(xcoBalance);
    const qty = parseFloat(quantity);
    if (xcoBal < qty) {
      alert(`Saldo XCO insuficiente. Tienes ${xcoBal} XCO.`);
      return;
    }

    setIsBuying(true);
    setStatusMsg('Inicializando Venta de XCO por CLP...');
    const txLabel = 'Venta XCO a CLP';

    try {
      if (!user) { throw new Error("Usuario no autenticado."); }
      if (!xcoinContract) { throw new Error("Contrato XCoin no cargado."); }

      await validateChainBeforeTx(wallet);

      const ADMIN_ADDRESS = "0x872Fd455e2B1Bb8B308BD09294F442ee820f5110";
      const amountWei = ethers.parseUnits(quantity.toString(), 18);

      // --- 1. Define Estimator ---
      const estimateFn = () => xcoinContract.transfer.estimateGas(ADMIN_ADDRESS, amountWei);

      // --- 2. Define Execution Logic ---
      const executeSellToCLP = async (gasLimit) => {
        setIsBuying(true);
        setStatusMsg('Por favor confirma la transferencia de XCO en tu wallet...');
        showTxOverlay('waiting-wallet', 1, 1, 'Confirma la transferencia de XCO en tu wallet...');

        try {
          const txResult = await raceTxWithPoll(
            () => xcoinContract.transfer(ADMIN_ADDRESS, amountWei, { gasLimit }),
            () => checkBalanceChanged(wallet.accounts[0].address, xcoinContract, 'XCO', false), // Check XCO decrease
            WALLET_TIMEOUT
          );

          let txHash = '';
          if (txResult.source === 'wc') {
            txHash = txResult.tx.hash;
            showTxOverlay('mining', 1, 1, 'Transferencia enviada. Esperando confirmación...', txHash);
            await withTimeout(txResult.tx.wait(), TX_TIMEOUT, 'Tiempo de espera agotado.');
          } else {
            txHash = 'on-chain-detected'; // Or try to find hash?
            showTxOverlay('mining', 1, 1, 'Transferencia detectada en cadena.');
          }

          sendTxNotification('✅ Transferencia Confirmada — XCoin', 'Vuelve a XCoin, estamos acreditando tu saldo CLP.');

          // 3. Call Backend to Credit CLP
          showTxOverlay('mining', 1, 1, 'Verificando en Backend... Acreditando CLP.');
          setStatusMsg('Verificando en Backend (Acreditando CLP)...');
          const token = await user.getIdToken();

          const response = await fetch('https://southamerica-west1-xcoin-plataforma.cloudfunctions.net/realizarVenta', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              data: {
                txHash: txHash,
                cantidad: Number(quantity),
                totalCLP: Number(quote.clpTotal)
              }
            })
          });

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || data.message || "Error en backend");
          }

          if (data.success) {
            hideTxOverlay();
            setTransactionData({
              type: 'venta',
              crypto: 'XCO',
              amount: quantity,
              payment: { amount: quote.clpTotal, currency: 'CLP' },
              gas: { bnb: 0, clp: 0 },
              txHash: txHash,
              timestamp: Date.now(),
              success: true
            });
            setShowSuccessModal(true);
            setStatusMsg('¡Venta completada!');
            sendTxNotification('✅ Venta XCO Completada — XCoin', '¡Tu saldo CLP ha sido acreditado!');

            setQuantity(0);
            setQuote(null);
            setAllowanceXCO('0');
            if (typeof refreshBalances === 'function') refreshBalances();
          }

        } catch (err) {
          console.error("Sell Error:", err);
          if (isWalletConnectSessionError(err)) {
            try { await disconnectWallet(); } catch (_) { }
            const sessionMsg = 'Sesión de wallet expirada. Vuelve a conectar tu wallet.';
            handleTxError(new Error(sessionMsg), 'Sesión Expirada');
          } else {
            handleTxError(err, 'Error en Venta XCO');
          }
        } finally {
          setIsBuying(false);
        }
      };

      // --- 3. Run Preparation Flow ---
      await prepareTransaction(
        toTitleCase(txLabel),
        estimateFn,
        executeSellToCLP,
        `Confirmar Venta XCO`,
        `Vender XCO`
      );

    } catch (err) {
      console.error(err);
      handleTxError(err, 'Error inicializando venta XCO');
      setIsBuying(false);
    }
  };

  // Helper needed? toTitleCase? Or just use string.
  const toTitleCase = (str) => str;


  const handleSellBNBToCLP = async () => {
    if (!quote) return;

    // 1. Validate BNB Balance
    const bnbBal = parseFloat(bnbBalance);
    const qty = parseFloat(quantity);
    if (bnbBal < qty) {
      alert(`Saldo BNB insuficiente. Tienes ${bnbBal} BNB.`);
      return;
    }

    setIsBuying(true);
    setStatusMsg('Inicializando Venta BNB por CLP...');
    const txLabel = 'Venta BNB a CLP';

    try {
      if (!user) { throw new Error("Usuario no autenticado."); }
      if (!wallet) { throw new Error("Wallet no conectada."); }

      await validateChainBeforeTx(wallet);

      const BNB_BUYBACK_ADDRESS = "0x6DeF104b6542d323Ef6a2Bebad60309E85196d95";
      const amountWei = ethers.parseEther(quantity.toString());

      // --- 1. Define Estimator (Using Public Provider) ---
      const estimateFn = async () => {
        const publicProvider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
        const est = await publicProvider.estimateGas({
          from: wallet.accounts[0].address,
          to: BNB_BUYBACK_ADDRESS,
          value: amountWei,
          data: '0x'
        });
        return est;
      };

      // --- 2. Define Execution Logic ---
      const executeSellBNBToCLP = async (gasLimit) => {
        setIsBuying(true);
        setStatusMsg('Por favor confirma la transferencia de BNB en tu wallet...');
        showTxOverlay('waiting-wallet', 1, 1, 'Confirma la transferencia de BNB en tu wallet...');

        try {
          // Send via EIP-1193 request directly (bypasses ethers.js to avoid WalletConnect 5201)
          // Inject gasLimit from estimation
          const txHash = await wallet.provider.request({
            method: 'eth_sendTransaction',
            params: [{
              from: wallet.accounts[0].address,
              to: BNB_BUYBACK_ADDRESS,
              value: ethers.toQuantity(amountWei),
              data: '0x',
              gas: ethers.toQuantity(gasLimit)
            }]
          });

          showTxOverlay('mining', 1, 1, 'Transferencia BNB enviada. Esperando confirmación...', txHash);
          setStatusMsg('Esperando confirmación de la red...');

          // Wait for confirmation using public RPC
          const publicProvider = new ethers.JsonRpcProvider('https://bsc-dataseed.binance.org/');
          const receipt = await withTimeout(
            publicProvider.waitForTransaction(txHash, 1),
            TX_TIMEOUT,
            'Tiempo de espera agotado esperando confirmación de la red.'
          );
          if (!receipt || receipt.status !== 1) {
            throw new Error("Transacción falló en blockchain");
          }
          sendTxNotification('✅ Transferencia BNB Confirmada — XCoin', 'Vuelve a XCoin, estamos acreditando tu saldo CLP.');

          // 3. Call Backend to Credit CLP
          showTxOverlay('mining', 1, 1, 'Verificando en Backend... Acreditando CLP.');
          setStatusMsg('Verificando en Backend (Acreditando CLP)...');
          const token = await user.getIdToken();

          const response = await fetch('https://southamerica-west1-xcoin-plataforma.cloudfunctions.net/realizarVentaBNB', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              data: {
                txHash: txHash,
                amountBNB: Number(quantity),
                paymentUSDT: Number(quote.paymentUSDT),
                totalCLP: Number(quote.clpTotal)
              }
            })
          });

          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || data.message || "Error en backend");
          }

          if (data.success) {
            hideTxOverlay();
            setTransactionData({
              type: 'venta',
              crypto: 'BNB',
              amount: quantity,
              payment: { amount: Number(quote.clpTotal), currency: 'CLP' },
              gas: { bnb: 0, clp: 0 }, // TODO: Fetch receipt gas used
              txHash: txHash,
              timestamp: Date.now(),
              success: true
            });
            setShowSuccessModal(true);
            if (typeof refreshBalances === 'function') refreshBalances();
            setQuantity(0);
            setQuote(null);
          }

        } catch (err) {
          console.error(err);
          handleTxError(err, 'Venta BNB Fallida');
        } finally {
          setIsBuying(false);
        }
      };

      // --- 3. Run Preparation Flow ---
      await prepareTransaction(
        txLabel,
        estimateFn,
        executeSellBNBToCLP,
        `Confirmar Venta BNB`,
        `Vender BNB`
      );

    } catch (err) {
      console.error(err);
      handleTxError(err, 'Error inicializando venta BNB');
      setIsBuying(false);
    }
  };


  const handleBuyWithCLP = async () => {
    if (!quote) return;

    // NOTA: La validación de mínimos ahora está en el input de cantidad (ver MINIMUM_AMOUNTS)

    const currentBalance = balances?.CLP || 0;
    if (currentBalance < quote.clpTotal) {
      setTransactionData({
        type: 'compra',
        crypto: selectedCoinKey,
        amount: quantity,
        payment: {
          amount: quote.clpTotal,
          currency: 'CLP'
        },
        gas: {
          bnb: 0,
          clp: 0
        },
        txHash: '',
        timestamp: Date.now(),
        success: false,
        error: `Saldo Insuficiente. Tienes $${currentBalance.toLocaleString()}, necesitas $${parseInt(quote.clpTotal).toLocaleString()}`
      });
      setShowSuccessModal(true);
      return;
    }

    setIsBuying(true);
    setStatusMsg('Procesando compra con Saldo Interno (Backend HTTPS v3.0)...');
    console.log("--> Iniciando compra CLP (Client v3.1.9 -> Backend v3.0 HTTPS)");

    try {
      if (!user) {
        throw new Error("Usuario no autenticado.");
      }

      // 1. Get Token
      const token = await user.getIdToken();

      // 2. Call Function via HTTPS (Bypassing Callable Middleware)
      let endpoint = 'https://southamerica-west1-xcoin-plataforma.cloudfunctions.net/realizarCompra';
      let payload = {
        data: {
          cantidad: selectedCoinKey === 'BNB' ? Number(quote.bnbAmount) : Number(quantity),
          moneda: selectedCoinKey, // 'XCO' or 'BNB'
          totalCLP: Number(quote.clpTotal)
        }
      };

      // USE LOCAL PYTHON BACKEND FOR BNB
      if (selectedCoinKey === 'BNB') {
        endpoint = '/api-bnb/buy';
        payload = {
          cantidad: Number(quote.bnbAmount),
          totalCLP: Number(quote.clpTotal),
          moneda: 'BNB'
        };
      }

      // 2. Call Function via HTTPS (Bypassing Callable Middleware)
      // Note: Cloud Functions expect wrapped 'data' body or not? HTTPS trigger v2 expects simple JSON usually.
      // But my previous code used { data: ... } wrapper for Cloud Func.
      // Python backend expects direct JSON.
      // So I will conditionalize body structure if needed, or update Python to handle both.
      // Python code I wrote: `data = request.get_json(); amount_bnb = data.get('cantidad')`
      // So Python expects direct keys or I should verify.
      // My python code: `amount_bnb = float(data.get('cantidad'))`.
      // So Python expects { cantidad: ..., totalCLP: ... } at root.
      // Cloud Function logic: `const data = req.body.data || req.body;`. It handles both.
      // So safest is to send direct JSON for Python, and Wrapped for Cloud Function (if needed).

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(selectedCoinKey === 'BNB' ? payload : { data: payload.data })
      });

      // 3. Handle Response
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `Error ${response.status}: ${data.message || 'Desconocido'}`);
      }

      if (data.success) {
        // NOTE: Backend Python ya registra la transacción en Firestore (tipo: 'compra_bnb')
        // No duplicar el registro aquí para evitar entradas dobles en el historial

        setTransactionData({
          type: 'compra',
          crypto: selectedCoinKey,
          amount: selectedCoinKey === 'BNB' ? quote.bnbAmount : quantity,
          payment: {
            amount: quote.clpTotal,
            currency: 'CLP'
          },
          gas: {
            bnb: 0,
            clp: 0
          },
          txHash: data.txHash,
          timestamp: Date.now(),
          success: true
        });
        setShowSuccessModal(true);
        setStatusMsg('¡Éxito! Fondos Enviados a tu Wallet.');
        setQuantity(0);
        setQuote(null);
        if (typeof refreshBalances === 'function') refreshBalances();
      } else {
        throw new Error(data.message || "La operación no reportó éxito.");
      }

    } catch (err) {
      console.error("Purchase Error:", err);
      // Show EXACT error from backend
      setTransactionData({
        type: 'compra',
        crypto: selectedCoinKey,
        amount: quantity,
        payment: {
          amount: quote.clpTotal,
          currency: 'CLP'
        },
        gas: {
          bnb: 0,
          clp: 0
        },
        txHash: '',
        timestamp: Date.now(),
        success: false,
        error: err.message
      });
      setShowSuccessModal(true);
      alert(`Error en la compra:\n${err.message}`);
      setStatusMsg("Error procesando compra CLP");
    } finally {
      setIsBuying(false);
    }
  };


  const handleFormSubmit = (e) => {
    e.preventDefault();

    // BNB Logic
    if (selectedCoinKey === 'BNB') {
      if (tradeType === 'Comprar') {
        if (paymentMethod === 'CLP') handleBuyWithCLP(); // CLP → Backend Python
        else onSubmit({ coin: 'BNB', tradeType: 'Comprar', orderType, quantity, price }); // USDT → Web3
      } else {
        // VENTA BNB
        if (paymentMethod === 'CLP') handleSellBNBToCLP(); // CLP → Cloud Function
        else onSubmit({ coin: 'BNB', tradeType: 'Vender', orderType, quantity, price }); // USDT → Web3
      }
      return;
    }

    // XCO Logic
    if (selectedCoinKey === 'XCO') {
      if (tradeType === 'Comprar') {
        if (paymentMethod === 'CLP') handleBuyWithCLP(); // CLP → Cloud Function
        else onSubmit({ coin: 'XCO', tradeType: 'Comprar', orderType, quantity, price }); // USDT → Web3
      } else {
        if (paymentMethod === 'CLP') handleSellToCLP();
        else onSubmit({ coin: 'XCO', tradeType: 'Vender', orderType, quantity, price }); // USDT → Web3
      }
      return;
    }

    // Otras monedas
    onSubmit({ coin: selectedCoinKey, tradeType, orderType, quantity, price });
  };


  if (!selectedCoin) {
    return (
      <form className="card buy-sell-widget">
        <div style={{ padding: '40px', textAlign: 'center' }}><p>Cargando...</p></div>
      </form>
    );
  }

  const isXCoin = selectedCoinKey === 'XCO';
  const isBNB = selectedCoinKey === 'BNB';
  const isComingSoon = !isXCoin && !isBNB; // Any coin other than XCO or BNB is Coming Soon

  const totalUSDT = selectedCoinKey === 'XCO'
    ? (quantity * 10)
    : (selectedCoinKey === 'BNB' ? (quote?.costUSDT || quantity * (bnbLivePrice || 0)) : Number(quantity));

  // Logic to determine if Buyback has liquidity
  const isLiquiditySufficient = (paymentMethod === 'CLP')
    ? true
    : (selectedCoinKey === 'BNB'
      ? parseFloat(bnbBuybackLiquidity) >= (tradeType === 'Vender' ? (quote?.paymentUSDT || 0) : 0) // Placeholder logic for BNB
      : parseFloat(buybackLiquidity) >= totalUSDT);

  // For BNB Sell, we need to know the USDT payment, but before quote we can estimate
  const estimatedUSDT = selectedCoinKey === 'BNB' ? (quantity * (bnbLivePrice || 0)) : totalUSDT;
  const hasLiquidity = selectedCoinKey === 'BNB'
    ? (paymentMethod === 'CLP' ? true : parseFloat(bnbBuybackLiquidity) > 0) // CLP no requiere liquidez USDT
    : parseFloat(buybackLiquidity) >= totalUSDT;

  const canSellAuto = tradeType === 'Vender' && (isXCoin || isBNB) && (selectedCoinKey === 'BNB' ? hasLiquidity : isLiquiditySufficient);

  // New Validation: Check if user has enough XCO to sell
  const exceedingBalance = tradeType === 'Vender' && isXCoin && (parseFloat(quantity) > parseFloat(xcoBalance || 0));

  // --- GAS CHECK LOGIC ---
  const GAS_THRESHOLD = 0.002; // ~1 USD
  const hasLowGas = parseFloat(bnbBalance) < GAS_THRESHOLD;

  // Always show indicator for Web3 ops
  const isWeb3Tx = (paymentMethod === 'USDT' || selectedCoinKey === 'BNB');

  const handleExpressRefill = () => {
    setSelectedCoinKey('BNB');
    setQuantity(3); // Suggest 3 USDT worth of BNB
    setTradeType('Comprar');
    setPaymentMethod('USDT'); // Assume they have USDT. If not, they might need CLP.
    // Ideally, check if they have USDT. If 0 USDT, suggest CLP?
    // User requested "Carga Express".
  };

  return (
    <form className="card buy-sell-widget" onSubmit={handleFormSubmit}>
      <div style={{ position: 'absolute', top: '5px', right: '10px', fontSize: '10px', color: '#666', opacity: 0.7 }}>{VERSION_STRING}</div>
      <div className="trade-type-tabs">
        <button type="button" className={`tab ${tradeType === 'Comprar' ? 'active buy' : ''}`} onClick={() => setTradeType('Comprar')}>Comprar</button>
        <button type="button" className={`tab ${tradeType === 'Vender' ? 'active sell' : ''}`} onClick={() => setTradeType('Vender')}>Vender</button>
      </div>

      <div className="market-tabs">
        <button type="button" className={`market-button ${selectedCoinKey === 'XCO' ? 'active' : ''}`} onClick={() => { setSelectedCoinKey('XCO'); setQuote(null); }}>XCoin</button>
        <button type="button" className={`market-button ${selectedCoinKey === 'BTC' ? 'active' : ''}`} onClick={() => setSelectedCoinKey('BTC')}>Bitcoin</button>
        <button type="button" className={`market-button ${selectedCoinKey === 'ETH' ? 'active' : ''}`} onClick={() => setSelectedCoinKey('ETH')}>Ethereum</button>
        <button type="button" className={`market-button ${selectedCoinKey === 'BNB' ? 'active' : ''}`} onClick={() => setSelectedCoinKey('BNB')}>BNB</button>
        <button type="button" className={`market-button ${selectedCoinKey === 'USDT' ? 'active' : ''}`} onClick={() => setSelectedCoinKey('USDT')}>USDT</button>
      </div>

      {/* --- COMING SOON PLACEHOLDER FOR NON-XCO --- */}
      {isComingSoon && (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: '#888',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '15px'
        }}>
          <div style={{ fontSize: '2rem', opacity: 0.5 }}>🚧</div>
          <h3 style={{ color: '#ccc' }}>Próximamente</h3>
          <p style={{ fontSize: '0.9rem' }}>
            El trading directo de {selectedCoin?.name || selectedCoinKey} estará disponible muy pronto.
            <br />Por ahora, solo XCoin está habilitado.
          </p>
        </div>
      )}

      {/* --- PAYMENT METHOD TOGGLE (XCoin & BNB) --- */}
      {(isXCoin || isBNB) && (
        <>
          <div style={{ margin: '15px 0', padding: '10px', background: '#16213e', borderRadius: '8px' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '8px', color: '#ccc' }}>
              Método de Pago:
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => { setPaymentMethod('USDT'); setQuote(null); }}
                style={{
                  flex: 1, padding: '8px', borderRadius: '5px',
                  border: paymentMethod === 'USDT' ? '1px solid #00d2ff' : '1px solid transparent',
                  background: paymentMethod === 'USDT' ? 'rgba(0, 210, 255, 0.1)' : '#0f3460',
                  color: paymentMethod === 'USDT' ? '#00d2ff' : '#888', fontWeight: 'bold'
                }}
              >
                USDT (Web3)
              </button>
              <button
                type="button"
                onClick={() => { setPaymentMethod('CLP'); setQuote(null); }}
                style={{
                  flex: 1, padding: '8px', borderRadius: '5px',
                  border: paymentMethod === 'CLP' ? '1px solid #ffd700' : '1px solid transparent',
                  background: paymentMethod === 'CLP' ? 'rgba(255, 215, 0, 0.1)' : '#0f3460',
                  color: paymentMethod === 'CLP' ? '#ffd700' : '#888', fontWeight: 'bold'
                }}
              >
                Saldo CLP
              </button>
            </div>
            {paymentMethod === 'CLP' && balances && (
              <div style={{ marginTop: '10px', textAlign: 'right', fontSize: '0.85rem', color: (balances.CLP || 0) > 0 ? '#4caf50' : '#ff4444' }}>
                Saldo Disponible: ${(balances.CLP || 0).toLocaleString()} CLP
              </div>
            )}
            {paymentMethod === 'USDT' && (
              <div style={{ marginTop: '10px', textAlign: 'right', fontSize: '0.85rem', color: parseFloat(usdtBalance) > 0 ? '#4caf50' : '#ff4444' }}>
                Saldo Disponible: {parseFloat(usdtBalance).toFixed(2)} USDT
              </div>
            )}
          </div>
        </>
      )}

      {/* --- GAS INDICATOR (ALWAYS SHOW FOR WEB3) --- */}
      {isWeb3Tx && !isComingSoon && (
        <div style={{
          padding: '15px',
          margin: '15px 0',
          background: hasLowGas ? 'rgba(255, 193, 7, 0.1)' : 'rgba(76, 175, 80, 0.1)', // Yellow vs Green BG
          border: hasLowGas ? '1px solid #ffc107' : '1px solid #4caf50',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: hasLowGas ? '#ffc107' : '#4caf50' }}>
            <FaGasPump />
            <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
              {hasLowGas ? 'Gas BNB Insuficiente' : 'Gas BNB Suficiente'}
            </span>
          </div>

          {/* Details */}
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#333' }}>
            Tienes <b>{parseFloat(bnbBalance).toFixed(4)} BNB</b>.
            {hasLowGas
              ? " Necesitas recargar para operar."
              : " Tienes saldo suficiente para cubrir la comisión de red."
            }
            {hasLowGas && parseFloat(bnbBalance) < 0.0005 && (
              <span style={{ display: 'block', marginTop: '5px', color: '#ff4444', fontWeight: 'bold' }}>
                Sin Gas Crítico. Opera con CLP o Recarga BNB.
              </span>
            )}
          </p>

          {/* Actions (Only show Info/Refill if Low, otherwise just clean info) */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setShowGasModal(true)}
              style={{
                background: 'transparent',
                border: '1px solid #777',
                color: '#ccc',
                borderRadius: '4px',
                padding: '5px 10px',
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
            >
              (i) {hasLowGas ? 'Saber mas' : 'Info Gas'}
            </button>

            {hasLowGas && parseFloat(bnbBalance) < 0.0005 ? (
              <button
                type="button"
                onClick={() => {
                  setSelectedCoinKey('BNB');
                  setTradeType('Comprar');
                  setPaymentMethod('CLP');
                  setQuantity(3); // 3 USDT value
                }}
                style={{
                  background: '#ffd700', border: 'none', color: '#000',
                  borderRadius: '4px', padding: '5px 10px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer'
                }}
              >
                Comprar con Saldo CLP
              </button>
            ) : hasLowGas ? (
              <button
                type="button"
                onClick={handleExpressRefill}
                style={{ background: '#00d2ff', border: 'none', color: '#000', borderRadius: '4px', padding: '5px 10px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Recargar (USDT)
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* --- GAS EDUCATION MODAL --- */}
      {
        showGasModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#1a1a2e', padding: '25px', borderRadius: '15px', width: '90%', maxWidth: '400px', border: '1px solid #444', color: '#fff' }}>
              <h3 style={{ marginTop: 0, color: '#00d2ff', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaGasPump /> Comisiones de Red (Gas)
              </h3>

              <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                <div style={{ minWidth: '40px', color: '#ffc107', fontSize: '1.5rem' }}><FaInfoCircle /></div>
                <div>
                  <h4 style={{ margin: '0 0 5px 0' }}>¿Qué es el Gas?</h4>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#bbb' }}>Es el "combustible" digital necesario para procesar cualquier operación en la Blockchain (Binance Smart Chain).</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                <div style={{ minWidth: '40px', color: '#4caf50', fontSize: '1.5rem' }}><FaShieldAlt /></div>
                <div>
                  <h4 style={{ margin: '0 0 5px 0' }}>¿Por qué es necesario?</h4>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#bbb' }}>Garantiza que tu transacción sea segura, inmutable y procesada por la red mundial de validadores.</p>
                </div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '8px', fontSize: '0.75rem', color: '#aaa', fontStyle: 'italic', marginBottom: '20px' }}>
                Nota: Esta comisión NO la cobra XCoin. Se paga directamente a la red minera para validar tu operación.
              </div>

              <button
                type="button"
                onClick={() => setShowGasModal(false)}
                style={{ width: '100%', padding: '12px', background: '#00d2ff', border: 'none', borderRadius: '8px', color: '#000', fontWeight: 'bold', cursor: 'pointer' }}
              >
                Entendido
              </button>
            </div>
          </div>
        )
      }

      {
        statusMsg && (
          <div className="alert" style={{ margin: '15px 0', textAlign: 'center', fontSize: '0.9em', padding: '10px', borderRadius: '5px', backgroundColor: '#e3f2fd', color: '#0d47a1' }}>
            {statusMsg}
          </div>
        )
      }
      {/* --- FORM FIELDS (For XCoin AND BNB) --- */}
      {
        (isXCoin || isBNB) && (
          <>
            <div className="form-group">
              <label>{isBNB ? 'Precio Unitario de BNB' : 'Precio Unitario de XCO'}</label>
              <div className="input-with-label">
                <input type="text" value={isBNB ? (bnbLivePrice ? bnbLivePrice.toFixed(2) : 'Cargando...') : "10"} disabled={true} />
                <span>USDT</span>
              </div>

              {/* Balance Display */}
              {wallet && (
                <small className="balance" style={{ marginTop: '5px', display: 'block', textAlign: 'right', fontWeight: 'bold' }}>
                  Tu saldo en {selectedCoinKey}: {getCurrentAssetBalance().toFixed(selectedCoinKey === 'BNB' ? 4 : 2)} {selectedCoinKey}
                </small>
              )}
            </div>

            <div className="form-group">
              <label>{tradeType === 'Comprar' ? 'Cantidad a Comprar' : 'Cantidad a Vender'} ({selectedCoinKey})</label>
              <div className="input-with-label">
                <input
                  type="number"
                  step="any"
                  min={MINIMUM_AMOUNTS[selectedCoinKey]?.[tradeType === 'Comprar' ? 'compra' : 'venta'] || 0}
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                    setQuote(null);
                    setHasManuallyEdited(true);
                  }}
                  onWheel={(e) => e.target.blur()}
                  disabled={isBuying || isApproving || !!quote}
                />
                <span>{selectedCoinKey}</span>
              </div>
              {MINIMUM_AMOUNTS[selectedCoinKey] && parseFloat(quantity) > 0 && parseFloat(quantity) < MINIMUM_AMOUNTS[selectedCoinKey][tradeType === 'Comprar' ? 'compra' : 'venta'] && (
                <div style={{ color: '#ff6b6b', fontSize: '0.8rem', marginTop: '5px' }}>
                  Aviso: Mínimo: {MINIMUM_AMOUNTS[selectedCoinKey][tradeType === 'Comprar' ? 'compra' : 'venta']} {selectedCoinKey}
                </div>
              )}
            </div>

            {/* --- QUOTE & COUNTDOWN UI --- */}
            {quote && (
              <div style={{ background: '#1a1a2e', padding: '15px', borderRadius: '10px', margin: '20px 0', border: '1px solid #ffd700' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#ccc', fontSize: '0.9rem' }}>
                  <span>Tasa de Cambio (Buda):</span>
                  <span>1 USD ≈ ${parseInt(quote.exchangeRate).toLocaleString()} CLP</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.2rem', fontWeight: 'bold', color: 'white', marginBottom: '15px' }}>
                  <span>Total:</span>
                  <span style={{ color: '#ffd700' }}>
                    {paymentMethod === 'CLP' ?
                      `$${parseInt(quote.clpTotal).toLocaleString()} CLP` :
                      `${Number(selectedCoinKey === 'BNB' ? (tradeType === 'Comprar' ? quote.costUSDT : quote.paymentUSDT) : (selectedCoinKey === 'XCO' ? quantity * 10 : quantity)).toFixed(2)} USDT`
                    }
                  </span>
                </div>
                <div style={{ height: '6px', background: '#333', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(timeLeft / QUOTE_TIMEOUT) * 100}%`, background: timeLeft < 10 ? '#ff4d4d' : '#4caf50', transition: 'width 1s linear' }} />
                </div>
              </div>
            )}

            {/* EXCEEDING BALANCE ALERT */}
            {exceedingBalance && (
              <div style={{ marginBottom: '15px', padding: '10px', background: 'rgba(255, 68, 68, 0.1)', color: '#ff4444', borderRadius: '5px', fontSize: '0.9rem', textAlign: 'center' }}>
                Aviso: Saldo insuficiente. Tienes {getCurrentAssetBalance().toFixed(selectedCoinKey === 'BNB' ? 4 : 2)} {selectedCoinKey}.
              </div>
            )}

            {tradeType === 'Vender' && !exceedingBalance && (
              <div style={{ marginBottom: '15px' }}>
                {canSellAuto ? (
                  <div style={{ padding: '10px', background: 'rgba(76, 175, 80, 0.1)', color: '#4caf50', borderRadius: '5px', fontSize: '0.9rem' }}>
                    ✅ Venta Instantánea Disponible (Liquidez en Cajero OK)
                  </div>
                ) : (
                  <div style={{ padding: '10px', background: 'rgba(255, 68, 68, 0.1)', color: '#ff4444', borderRadius: '5px', fontSize: '0.9rem' }}>
                    Aviso: Cajero sin liquidez automática para este monto.<br />
                    Disponibles: {selectedCoinKey === 'BNB' ? parseFloat(bnbBuybackLiquidity).toFixed(2) : parseFloat(buybackLiquidity).toFixed(0)} USDT.<br />
                    <small>Por favor reduce el monto o contacta al administrador.</small>
                  </div>
                )}
              </div>
            )}

            {/* --- ACTION BUTTONS (XCOIN FLOW) --- */}
            <div style={{ marginTop: '20px' }}>
              {!wallet ? (
                <button type="button" className="buy-button" disabled>Conecta tu Billetera</button>
              ) : (
                <>
                  {tradeType === 'Comprar' && (
                    !quote ? (
                      <button type="button" className="buy-button" onClick={handleGetQuote} disabled={isQuoting || quantity <= 0 || (MINIMUM_AMOUNTS[selectedCoinKey] && parseFloat(quantity) < MINIMUM_AMOUNTS[selectedCoinKey].compra)} style={{ background: '#00d2ff', color: 'black' }}>
                        {isQuoting ? 'Cotizando...' : (paymentMethod === 'CLP' ? 'Cotizar (CLP)' : 'Cotizar (Visual)')}
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="button" style={{ flex: 1, padding: '10px', background: '#ff4444', border: 'none', borderRadius: '5px', color: 'white', cursor: 'pointer' }} onClick={() => { setQuote(null); setApprovedForCurrentQuote(false); }}>Cancelar</button>

                        {paymentMethod === 'USDT' ? (
                          parseFloat(usdtBalance) < totalUSDT ? (
                            <button type="button" className="buy-button" disabled style={{ flex: 2, margin: 0, background: '#555', cursor: 'not-allowed' }}>Saldo Insuficiente ({totalUSDT.toFixed(2)} USDT)</button>
                          ) : (
                            (!approvedForCurrentQuote || (selectedCoinKey === 'BNB' ? parseFloat(allowanceBNB) : parseFloat(allowanceUSDT)) < totalUSDT) ? (
                              <button type="button" className="buy-button" style={{ flex: 2, margin: 0 }} onClick={handleApprove} disabled={isApproving}>{isApproving ? 'Aprobando...' : `Aprobación USDT (Paso 1 de 2)`}</button>
                            ) : (
                              <button type="button" className="buy-button" style={{ flex: 2, margin: 0 }} onClick={handleTransact} disabled={isBuying}>{isBuying ? 'Procesando...' : `Compra (Paso 2 de 2)`}</button>
                            )
                          )
                        ) : (
                          <button type="button" className="buy-button" style={{ flex: 2, margin: 0, background: '#ffd700', color: 'black' }} onClick={handleBuyWithCLP} disabled={isBuying}>{isBuying ? 'Procesando...' : `Confirmar Compra`}</button>
                        )}
                      </div>
                    )
                  )}

                  {tradeType === 'Vender' && (
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column', width: '100%' }}>
                      {canSellAuto ? (
                        <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                          {paymentMethod === 'CLP' ? (
                            !quote ? (
                              <button type="button" className="buy-button" onClick={handleGetQuote} disabled={isQuoting || quantity <= 0 || (MINIMUM_AMOUNTS[selectedCoinKey] && parseFloat(quantity) < MINIMUM_AMOUNTS[selectedCoinKey].venta)} style={{ background: '#ffd700', color: 'black', width: '100%' }}>
                                {isQuoting ? 'Cotizando...' : 'Cotizar Venta (CLP)'}
                              </button>
                            ) : (
                              <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                <button type="button" style={{ flex: 1, padding: '10px', background: '#ff4444', border: 'none', borderRadius: '5px', color: 'white', cursor: 'pointer' }} onClick={() => setQuote(null)}>Cancelar</button>
                                <button type="button" className="buy-button" onClick={selectedCoinKey === 'BNB' ? handleSellBNBToCLP : handleSellToCLP} disabled={isBuying || exceedingBalance} style={{ flex: 2, background: '#ffd700', color: 'black', margin: 0 }}>
                                  {isBuying ? 'Procesando...' : 'Confirmar Venta CLP'}
                                </button>
                              </div>
                            )
                          ) : selectedCoinKey === 'BNB' ? (
                            !quote ? (
                              <button type="button" className="buy-button" onClick={handleGetBNBSellQuote} disabled={!contractsReady || isQuoting || quantity <= 0} style={{ width: '100%' }}>
                                {!contractsReady ? 'Inicializando contratos...' : (isQuoting ? 'Cotizando...' : 'Cotizar Venta BNB (USDT)')}
                              </button>
                            ) : (
                              <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                                <button type="button" style={{ flex: 1, padding: '10px', background: '#ff4444', border: 'none', borderRadius: '5px', color: 'white', cursor: 'pointer' }} onClick={() => setQuote(null)}>Cancelar</button>
                                <button type="button" className="buy-button" onClick={handleTransact} disabled={!contractsReady || isBuying || exceedingBalance} style={{ flex: 2, margin: 0 }}>
                                  {!contractsReady ? 'Contratos no listos' : (isBuying ? 'Procesando...' : 'Confirmar Venta BNB')}
                                </button>
                              </div>
                            )
                          ) : (
                            quantity <= 0 ? (
                              <button type="button" className="buy-button" disabled style={{ width: '100%', background: '#555', cursor: 'not-allowed' }}>Vender XCO</button>
                            ) : (!approvedForCurrentQuote || parseFloat(allowanceXCO) < quantity) ? (
                              <button type="button" className="buy-button" onClick={handleApprove} disabled={isApproving || exceedingBalance} style={{ width: '100%' }}>{isApproving ? 'Aprobando...' : 'Aprobación XCO (Paso 1 de 2)'}</button>
                            ) : (
                              <button type="button" className="buy-button" onClick={handleTransact} disabled={isBuying || exceedingBalance} style={{ width: '100%' }}>{isBuying ? 'Procesando...' : `Venta (Paso 2 de 2)`}</button>
                            )
                          )}
                        </div>
                      ) : (
                        <button type="button" className="buy-button" disabled style={{ background: '#555', cursor: 'not-allowed', width: '100%' }}>Venta no disponible (Sin liquidez)</button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )
      }

      <TransactionProgressOverlay
        visible={txPhase !== 'idle'}
        phase={txPhase}
        currentStep={txStep}
        totalSteps={txTotalSteps}
        message={txOverlayMsg}
        txHash={txOverlayHash}
        errorMsg={txOverlayError}
        onDismiss={() => {
          hideTxOverlay();
          if (pendingSuccessModal.current) {
            pendingSuccessModal.current = false;
            setShowSuccessModal(true);
          }
        }}
      />

      <TransactionSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        transactionData={transactionData}
      />

      <TransactionConfirmModal
        isOpen={showConfirmModal}
        onClose={cancelPending}
        title={modalTitle}
        status={gasStatus}
        gasEstimate={estimatedGasCost}
        errorMessage={modalError}
        onConfirm={executePending}
        actionLabel={modalActionLabel}
      />

    </form >
  );
};

export default TradingWidget;